import { Debug } from "./debug";
import { Game } from "./game";

type PerfMetricEntry = {
	name: string;
	subname: string;
	min: number;
	max: number;
	total: number;
	count: number;
	firstFrame: number;
};


export class PerformanceTracker {
	metrics = new Map<string, PerfMetricEntry>();

	currentSystem: string = "";
	systemStart = -1;
	currentSubSystem: string = "";
	subSystemStart = -1;

	constructor(private getTime: () => number = () => performance.now()) {
	}

	startSystem(systemName: string) {
		if (Debug.SystemPerformanceTracking === false) return;
		this.currentSystem = systemName;
		this.systemStart = this.getTime();
	}

	startSubsystem(subname: string) {
		if (Debug.SystemPerformanceTracking === false) return;
		this.currentSubSystem = subname;
		this.subSystemStart = this.getTime();
	}

	endSystem() {
		if (Debug.SystemPerformanceTracking === false) return;
		if (this.systemStart === -1 || this.currentSystem === "") {
			return;
		}
		this.makeEntry(this.currentSystem, "", this.getTime() - this.systemStart);
		this.systemStart = -1;
		this.currentSystem = "";
	}

	endSubsystem() {
		if (Debug.SystemPerformanceTracking === false) return;
		if (this.subSystemStart === -1 || this.currentSubSystem === "") {
			return;
		}
		this.makeEntry(this.currentSystem, this.currentSubSystem, this.getTime() - this.subSystemStart);
		this.subSystemStart = -1;
		this.currentSubSystem = "";
	}

	makeEntry(systemName: string, subname: string, duration: number) {
		if (Debug.SystemPerformanceTracking === false) return;
		const key = `${systemName}.${subname}`;
		let entry = this.metrics.get(key);
		if (!entry) {
			entry = { min: Infinity, max: -Infinity, total: 0, count: 0, firstFrame: duration, name: systemName, subname: subname };
			this.metrics.set(key, entry);
		}
		entry.min = Math.min(entry.min, duration);
		entry.max = Math.max(entry.max, duration);
		entry.total += duration;
		entry.count++;
	}

	output() {
		if (Debug.SystemPerformanceTracking === false) return;
		const allMetrics = Array.from(this.metrics.entries());
		const systemMetrics = allMetrics.filter(([_, m]) => m.subname === "");
		const subsystemMetrics = allMetrics.filter(([_, m]) => m.subname !== "");
		const subsystems = new Map<string, PerfMetricEntry[]>();
		subsystemMetrics.forEach(([_, m]) => {
			if (!subsystems.has(m.name)) {
				subsystems.set(m.name, []);
			}
			subsystems.get(m.name)!.push(m);
		});
		const sortedMetrics = systemMetrics.sort((a, b) => b[1].total - a[1].total);
		const header = "Performance metrics:".padEnd(29) + "Total".padEnd(10) + "Min".padEnd(10) + "Max".padEnd(10) + "Avg".padEnd(10) + "First Frame".padEnd(10);
		console.log(header);
		console.log("-".repeat(header.length));
		for (const [key, metrics] of sortedMetrics) {
			const avg = metrics.total / metrics.count;
			const name = metrics.name;
			console.log(`- ${name.padEnd(25)}: ${metrics.total.toFixed(2).padEnd(8)}  ${metrics.min.toFixed(2).padEnd(8)}  ${metrics.max.toFixed(2).padEnd(8)}  ${avg.toFixed(2).padEnd(8)}  ${metrics.firstFrame.toFixed(2).padEnd(8)}`);
			if (subsystems.has(name)) {
				const subMetrics = subsystems.get(name)!;
				subMetrics.sort((a, b) => b.total - a.total);
				for (const sub of subMetrics) {
					const subAvg = sub.total / sub.count;
					console.log(`  - ${sub.subname.padEnd(25)}: ${sub.total.toFixed(2).padEnd(8)}  ${sub.min.toFixed(2).padEnd(8)}  ${sub.max.toFixed(2).padEnd(8)}  ${subAvg.toFixed(2).padEnd(8)}  ${sub.firstFrame.toFixed(2).padEnd(8)}`);
				}
			}
		}
	}

	clear() {
		if (Debug.SystemPerformanceTracking === false) return;
		this.metrics.clear();
	}

}

export let PerfTracker = new PerformanceTracker();

//Set performance timers
export function setupSystemPerfTimers(game: Game, getTime: () => number = () => performance.now()) {
	if (!Debug.SystemPerformanceTracking) return;
	PerfTracker = new PerformanceTracker(getTime);
	let wasInSimulation = game.inSimulation;
	game.world.getSystems().forEach((system, i) => {
		const orig = system.execute;
		system.execute = (delta, time) => {
			if (game.inSimulation) {
				//Reset performance metrics at the start of simulation
				if (!wasInSimulation) {
					wasInSimulation = true;
					PerfTracker.clear();
				}

				PerfTracker.startSystem(system.constructor.name);
				orig.call(system, delta, time);
				PerfTracker.endSystem();
			}
			else {
				if (wasInSimulation) {
					wasInSimulation = false;
					PerfTracker.output();
				}
				orig.call(system, delta, time);
			}
		};
	});
}